import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import type { Color } from "@mui/material";
import { amber, blue, deepOrange, pink } from "@mui/material/colors";
import StandardReportAPDetailsIcon from "./icons/StandardReportAPDetailsIcon.tsx";
import StandardReportAPSummaryIcon from "./icons/StandardReportAPSummaryIcon.tsx";
import StandardReportARDetailsIcon from "./icons/StandardReportARDetailsIcon.tsx";
import StandardReportARSummaryIcon from "./icons/StandardReportARSummaryIcon.tsx";
import { pageRoutes } from "../../../../routes.ts";

export interface StandardReportDefinition {
  key: "balanceSheet" | "incomeStatement" | "arAgingSummary" | "apAgingSummary" | "arAgingDetail" | "apAgingDetail";
  title: string;
  description: string;
  route: string;
  Icon: React.ElementType;
  iconColor: Color;
}

export const standardReports: StandardReportDefinition[] = [
  {
    key: "balanceSheet",
    title: "Balance Sheet",
    description: "Shows the financial position at a specific date, including assets, liabilities, and equity.",
    route: pageRoutes.balanceSheet,
    Icon: BalanceRoundedIcon,
    iconColor: blue,
  },
  {
    key: "incomeStatement",
    title: "Income Statement",
    description: "Shows revenues and expenses over a period and summarizes net income.",
    route: pageRoutes.incomeStatement,
    Icon: InsertChartOutlinedRoundedIcon,
    iconColor: deepOrange,
  },
  {
    key: "arAgingSummary",
    title: "A/R Aging Summary Report",
    description: "Breaks down outstanding receivables by aging periods.",
    route: pageRoutes.arAgingSummary,
    Icon: StandardReportARSummaryIcon,
    iconColor: amber,
  },
  {
    key: "arAgingDetail",
    title: "A/R Aging Detail Report",
    description: "Detailed breakdown of outstanding receivables by aging periods.",
    route: pageRoutes.arAgingDetail,
    Icon: StandardReportARDetailsIcon,
    iconColor: amber,
  },
  {
    key: "apAgingSummary",
    title: "A/P Aging Summary Report",
    description: "Shows outstanding payables categorized by due date ranges.",
    route: pageRoutes.apAgingSummary,
    Icon: StandardReportAPSummaryIcon,
    iconColor: pink,
  },
  {
    key: "apAgingDetail",
    title: "A/P Aging Detail Report",
    description: "Detailed breakdown of outstanding payables by due date ranges.",
    route: pageRoutes.apAgingDetail,
    Icon: StandardReportAPDetailsIcon,
    iconColor: pink,
  },
];

const getRequiredReport = (key: StandardReportDefinition["key"]): StandardReportDefinition => {
  const report = standardReports.find((item) => item.key === key);
  if (!report) {
    throw new Error(`Missing standard report definition for '${key}'.`);
  }

  return report;
};

export const standardReportsByKey = {
  balanceSheet: getRequiredReport("balanceSheet"),
  incomeStatement: getRequiredReport("incomeStatement"),
  arAgingSummary: getRequiredReport("arAgingSummary"),
  apAgingSummary: getRequiredReport("apAgingSummary"),
  arAgingDetail: getRequiredReport("arAgingDetail"),
  apAgingDetail: getRequiredReport("apAgingDetail"),
};
